/**
 * Feedback widget configuration.
 *
 * Fetched once per session from `/feedback/config`. The response drives whether
 * the widget renders at all and mirrors the server-side upload limits so the
 * user gets instant validation instead of a rejected upload.
 */

import { useEffect, useState } from 'react';
import { apiService } from '../services';
import type { FeedbackConfig } from '../types/feedback';

const DISABLED: FeedbackConfig = {
  enabled: false,
  max_files: 6,
  max_file_bytes: 5 * 1024 * 1024,
  max_summary_length: 200,
  max_details_length: 5000,
  allowed_extensions: [],
};

let cached: Promise<FeedbackConfig> | null = null;

function loadConfig(): Promise<FeedbackConfig> {
  if (!cached) {
    cached = apiService.getFeedbackConfig().catch(() => DISABLED);
  }
  return cached;
}

export function useFeedbackConfig(): FeedbackConfig {
  const [config, setConfig] = useState<FeedbackConfig>(DISABLED);

  useEffect(() => {
    let active = true;
    loadConfig().then((loaded) => {
      if (active) setConfig(loaded);
    });
    return () => {
      active = false;
    };
  }, []);

  return config;
}
